import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import apiClient from '../services/apiClient';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { CircularProgress, Select, MenuItem, FormControl } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ReactGA from 'react-ga4';
import { getCurrency } from '../services/tableHelper';
import SalesChart, { SalesDataItem } from '../components/SalesChart';
import DatePeriodSelector from '../components/DatePeriodSelector';
import { format } from 'date-fns';
import { useSubscription } from '../hooks/useSubscription';
import * as routes from '../utils/routes';
import AllProductsModal from '../components/AllProductsModal';

const Wrapper = styled.div`
	background-color: white;
	width: 100%;
	min-height: 100vh;
	padding: 30px 40px;
	box-sizing: border-box;
	@media (max-width: 768px) {
		padding: 20px 10px;
		padding-top: 0px;
	}
`;

const AnalyticsFullGridWrapper = styled.div`
	display: flex;
	flex-direction: column;
`;

const DesktopOrderWrapper = styled.div`
	display: contents;

	@media (max-width: 768px) {
		display: flex;
		flex-direction: column;
	}
`;

const MobileOrderWrapper = styled.div<{ order: number }>`
	display: contents;

	@media (max-width: 768px) {
		display: flex;
		flex-direction: column;
		order: ${(props) => props.order};
	}
`;

const AnalyticsCardsContainer = styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
	//   box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
	border-bottom: 1px solid #eaeaea;
`;

const AnalyticsCardWrapper = styled.div`
	padding: 10px;

	@media (max-width: 768px) {
		width: 100%;
	}
`;

const AnalyticsCard = styled.div`
	background-color: white;
	padding: 30px 40px;
	border-radius: 15px;
	min-height: 128px;
	box-sizing: border-box;
`;

const AnalyticsCardLabel = styled.p<{ color?: string }>`
	color: ${({ color }) => color || '#666'};
	font-size: 14px;
	margin: 0px;
	margin-bottom: 5px;
`;

const AnalyticsCardValue = styled.h2<{ size?: 'small' | 'medium' | 'large' }>`
	margin: 0px;
	font-weight: 600;
	font-size: ${(props) => {
		switch (props.size) {
			case 'small':
				return '20px';
			case 'large':
				return '50px';
			case 'medium':
			default:
				return '28px'; // default to medium size if no size is provided
		}
	}};
`;

const StatsSection = styled.div`
	margin-top: 30px;
	background-color: white;
	//   border-radius: 15px;
	padding: 20px;
	//   box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const SectionTitle = styled.h3`
	font-size: 18px;
	font-weight: 600;
	margin-bottom: 20px;
	color: #333;
`;

const SectionHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 20px;
`;

const StatsGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 20px;

	& > :first-child {
		border-right: 1px solid #eaeaea;

		@media (max-width: 768px) {
			border-right: none;
			border-bottom: 1px solid #eaeaea;
		}
	}

	@media (max-width: 768px) {
		grid-template-columns: 1fr;
	}
`;

const StyledSelect = styled(Select)`
	&& {
		font-size: 14px;
		height: 36px;
		min-width: 150px;

		.MuiSelect-select {
			padding: 8px 12px;
		}
	}
`;

const StatsList = styled.div`
	//   background-color: #f9fafb;
	border-radius: 12px;
	padding: 16px;
`;

const ListItem = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 8px 0;
	//   border-bottom: 1px solid #e5e7eb;

	&:last-child {
		border-bottom: none;
	}
`;

const ListItemLabel = styled.span`
	font-size: 14px;
	color: #333;
	flex: 1;
	margin-right: 8px;
`;

const ListItemValue = styled.span`
	font-size: 14px;
	font-weight: 600;
	color: #333;
`;

const PendingOrdersSection = styled.div`
	margin-top: 30px;
	background-color: white;
	//   border-radius: 15px;
	padding: 20px;
	//   box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
	border-top: 1px solid #eaeaea;
`;

const PendingOrdersTableWrapper = styled.div`
	max-height: 400px;
	overflow-y: auto;
	//   border-radius: 8px;
	//   border: 1px solid #e5e7eb;

	/* Custom scrollbar styling */
	&::-webkit-scrollbar {
		width: 8px;
	}

	&::-webkit-scrollbar-track {
		background: #f3f4f6;
		border-radius: 4px;
	}

	&::-webkit-scrollbar-thumb {
		background: #d1d5db;
		border-radius: 4px;
	}

	&::-webkit-scrollbar-thumb:hover {
		background: #9ca3af;
	}
`;

const PendingOrdersTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	position: relative;
`;

const TableHeader = styled.th`
	text-align: left;
	padding: 12px;
	font-size: 12px;
	font-weight: 600;
	color: #666;
	border-bottom: 2px solid #e5e7eb;
	background-color: white;
	position: sticky;
	top: 0;
	z-index: 10;
`;

const TableCell = styled.td`
	padding: 12px;
	font-size: 14px;
	color: #333;
	border-bottom: 1px solid #f3f4f6;
`;

const ViewInvoiceButton = styled.button`
	background-color: #3b82f6;
	color: white;
	border: none;
	padding: 6px 12px;
	border-radius: 6px;
	font-size: 12px;
	cursor: pointer;
	transition: background-color 0.2s;

	&:hover {
		background-color: #2563eb;
	}
`;

const EmptyState = styled.div`
	text-align: center;
	padding: 40px;
	color: #666;
	font-size: 14px;
`;

const NoSubscriptionContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 60vh;
	text-align: center;
	padding: 40px 20px;
`;

const NoSubscriptionIcon = styled.div`
	margin-bottom: 24px;
`;

const NoSubscriptionTitle = styled.h2`
	font-size: 24px;
	font-weight: 600;
	color: #1f2937;
	margin-bottom: 16px;
	margin-top: 0;
`;

const NoSubscriptionMessage = styled.p`
	font-size: 16px;
	color: #6b7280;
	margin-bottom: 32px;
	max-width: 500px;
	line-height: 1.5;
`;

const SubscribeButton = styled.button`
	background-color: #2563eb;
	color: white;
	border: none;
	padding: 12px 24px;
	border-radius: 8px;
	font-size: 16px;
	font-weight: 600;
	cursor: pointer;
	transition: background-color 0.2s;

	&:hover {
		background-color: #1d4ed8;
	}
`;

interface TopProduct {
	product_id: number;
	product_name: string;
	total_quantity?: number;
	total_revenue?: number;
}

interface TopCustomer {
	customer_id: number;
	customer_name: string;
	customer_email?: string;
	customer_phone?: string;
	total_quantity?: number;
	total_revenue?: number;
}

interface PendingOrder {
	id: number;
	hash_id: string;
	date_created: string;
	backdated_order_date?: string;
	order_date: string;
	customer_name: string;
	customer_email?: string;
	customer_phone?: string;
	order_address?: string;
	order_contact?: string;
	order_amount: number;
}

interface Stats {
	total_sales: number;
	total_expenses: number;
	total_variants_sold: number;
	stock_qty_available: number;
	top_selling_variant: string;
	sales_data: SalesDataItem[];
	top_10_products_by_quantity: TopProduct[];
	top_10_products_by_revenue: TopProduct[];
	top_10_customers_by_quantity: TopCustomer[];
	top_10_customers_by_revenue: TopCustomer[];
	pending_payment_orders: PendingOrder[];
}

type Period = 'This week' | 'This month' | 'This year' | 'Custom Range';

export default function Dashboard() {
	const navigate = useNavigate();
	const [stats, setStats] = useState<Stats | null>(null);
	const [fromDate, setFromDate] = useState<Date>(new Date());
	const [toDate, setToDate] = useState<Date>(new Date());
	const [isFetching, setIsFetching] = useState<boolean>(false);
	const [hasSubscriptionError, setHasSubscriptionError] = useState<boolean>(false);
	const [selectedPeriod, setSelectedPeriod] = useState<Period>('This month');
	const [productViewType, setProductViewType] = useState<'quantity' | 'revenue'>('quantity');
	const [customerViewType, setCustomerViewType] = useState<'quantity' | 'revenue'>('quantity');
	const [showAllProductsModal, setShowAllProductsModal] = useState(false);

	const currency = useSelector((state: any) => getCurrency(state.auth.user.currency));
	const { subscription, isSubscriptionActive, isTrialing, isInGracePeriod } = useSubscription();

	const fetchStats = (from: Date, to: Date) => {
		// Only fetch stats if user has an active subscription
		if (!isSubscriptionActive && !isTrialing && !isInGracePeriod) {
			setHasSubscriptionError(true);
			return;
		}

		setIsFetching(true);
		setHasSubscriptionError(false);
		const fromStr = format(from, 'yyyy-MM-dd');
		const toStr = format(to, 'yyyy-MM-dd');

		apiClient
			.get(`/stats?from=${fromStr}&to=${toStr}`)
			.then((response: any) => {
				console.log('fetched stats', response.data);
				setStats(response.data);
			})
			.catch((error) => {
				console.log('error fetching stats', error);
				setHasSubscriptionError(true);
			})
			.finally(() => {
				setIsFetching(false);
			});
	};

	useEffect(() => {
		ReactGA.send({
			hitType: 'pageview',
			page: '/',
			title: 'Dashboard',
		});

		// Set initial dates to current month
		const today = new Date();
		const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
		const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

		setFromDate(firstDayOfMonth);
		setToDate(lastDayOfMonth);
	}, []);

	// Fetch stats when subscription status changes or dates change
	useEffect(() => {
		if (fromDate && toDate) {
			fetchStats(fromDate, toDate);
		}
	}, [isSubscriptionActive, isTrialing, isInGracePeriod, fromDate, toDate]);

	const handleDateRangeChange = (from: Date, to: Date, period: Period) => {
		setFromDate(from);
		setToDate(to);
		setSelectedPeriod(period);
		fetchStats(from, to);
	};

	const handleViewInvoice = (hashId: string) => {
		window.open(`/invoice?hash=${hashId}&skipTracking=true`, '_blank');
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		});
	};

	// Show no-subscription state when user doesn't have an active subscription
	const showNoSubscription =
		hasSubscriptionError || (!isSubscriptionActive && !isTrialing && !isInGracePeriod && !isFetching);

	if (showNoSubscription) {
		return (
			<Wrapper>
				<NoSubscriptionContainer>
					<NoSubscriptionIcon>
						<InfoIcon sx={{ fontSize: 40, color: '#2563eb' }} />
					</NoSubscriptionIcon>
					<NoSubscriptionTitle>Welcome to VendReady!</NoSubscriptionTitle>
					<NoSubscriptionMessage>
						Start tracking your sales and analytics by subscribing to a plan that fits your business needs.
					</NoSubscriptionMessage>
					<SubscribeButton onClick={() => navigate(routes.SUBSCRIPTION)}>Subscribe Now</SubscribeButton>
				</NoSubscriptionContainer>
			</Wrapper>
		);
	}

	if (!stats && !isFetching) {
		return (
			<Wrapper>
				<div style={{ padding: '20px', paddingTop: '20px' }}>
					<Header
						title="Dashboard"
						description="View stats and analytics"
						headerComponents={[
							<DatePeriodSelector
								key="date-period-selector"
								onDateRangeChange={handleDateRangeChange}
								initialPeriod="This month"
							/>,
						]}
					/>
				</div>
				<div
					style={{
						width: '100%',
						height: '50vh',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
					}}
				>
					<CircularProgress />
				</div>
			</Wrapper>
		);
	}

	return (
		<Wrapper>
			<div style={{ padding: '20px', paddingTop: '20px' }}>
				<Header
					title="Dashboard"
					description="View stats and analytics"
					headerComponents={[
						<DatePeriodSelector
							key="date-period-selector"
							onDateRangeChange={handleDateRangeChange}
							initialPeriod="This month"
						/>,
					]}
				/>
			</div>
			{isFetching ? (
				<div
					style={{
						width: '100%',
						height: '50%',
						flex: 1,
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						alignItems: 'center',
					}}
				>
					<CircularProgress />
				</div>
			) : (
				<AnalyticsFullGridWrapper>
					{/* Sales Chart - Order 1 on mobile */}
					<MobileOrderWrapper order={1}>
						<SalesChart
							salesData={stats?.sales_data}
							initialStartDate={fromDate}
							initialEndDate={toDate}
							selectedPeriod={selectedPeriod}
						/>
					</MobileOrderWrapper>

					{/* Analytics Cards - Order 2 on mobile */}
					<MobileOrderWrapper order={2}>
						<AnalyticsCardsContainer>
							<AnalyticsCardWrapper>
								<AnalyticsCard>
									<AnalyticsCardLabel color="#10b981">Total Sales</AnalyticsCardLabel>
									<AnalyticsCardValue>
										{currency}
										{stats ? Number(stats.total_sales).toFixed(2) : '0.00'}
									</AnalyticsCardValue>
								</AnalyticsCard>
							</AnalyticsCardWrapper>
							<AnalyticsCardWrapper>
								<AnalyticsCard>
									<AnalyticsCardLabel color="#ef4444">Total Expenses</AnalyticsCardLabel>
									<AnalyticsCardValue>
										{currency}
										{stats ? Number(stats.total_expenses).toFixed(2) : '0.00'}
									</AnalyticsCardValue>
								</AnalyticsCard>
							</AnalyticsCardWrapper>
							<AnalyticsCardWrapper>
								<AnalyticsCard>
									<AnalyticsCardLabel color="#F08763">Total Units Sold</AnalyticsCardLabel>
									<AnalyticsCardValue>
										{stats?.total_variants_sold.toLocaleString()}
									</AnalyticsCardValue>
								</AnalyticsCard>
							</AnalyticsCardWrapper>
							<AnalyticsCardWrapper>
								<AnalyticsCard>
									<AnalyticsCardLabel>Available Stock Qty</AnalyticsCardLabel>
									<AnalyticsCardValue>
										{stats?.stock_qty_available.toLocaleString()}
									</AnalyticsCardValue>
								</AnalyticsCard>
							</AnalyticsCardWrapper>
							<AnalyticsCardWrapper>
								<AnalyticsCard>
									<AnalyticsCardLabel>Top Selling Product</AnalyticsCardLabel>
									<AnalyticsCardValue size="small">{stats?.top_selling_variant}</AnalyticsCardValue>
								</AnalyticsCard>
							</AnalyticsCardWrapper>
						</AnalyticsCardsContainer>
					</MobileOrderWrapper>

					{/* Top Products and Customers Grid - Order 3 on mobile */}
					<MobileOrderWrapper order={3}>
						<StatsGrid>
							{/* Top Products Section */}
							<StatsSection>
								<SectionHeader>
									<SectionTitle style={{ marginBottom: 0 }}>Top 10 Products</SectionTitle>
									<FormControl size="small">
										<StyledSelect
											value={productViewType}
											onChange={(e) =>
												setProductViewType(e.target.value as 'quantity' | 'revenue')
											}
										>
											<MenuItem value="quantity">By Quantity</MenuItem>
											<MenuItem value="revenue">By Revenue</MenuItem>
										</StyledSelect>
									</FormControl>
								</SectionHeader>
								<StatsList>
									{productViewType === 'quantity' ? (
										stats?.top_10_products_by_quantity &&
										stats.top_10_products_by_quantity.length > 0 ? (
											stats.top_10_products_by_quantity.map((product, index) => (
												<ListItem key={product.product_id}>
													<ListItemLabel>
														{index + 1}. {product.product_name}
													</ListItemLabel>
													<ListItemValue>
														{product.total_quantity
															? product.total_quantity.toLocaleString()
															: '0'}{' '}
														units
													</ListItemValue>
												</ListItem>
											))
										) : (
											<EmptyState>No products sold in this period</EmptyState>
										)
									) : stats?.top_10_products_by_revenue &&
									  stats.top_10_products_by_revenue.length > 0 ? (
										stats.top_10_products_by_revenue.map((product, index) => (
											<ListItem key={product.product_id}>
												<ListItemLabel>
													{index + 1}. {product.product_name}
												</ListItemLabel>
												<ListItemValue>
													{currency}
													{product.total_revenue
														? Number(product.total_revenue).toFixed(2)
														: '0.00'}
												</ListItemValue>
											</ListItem>
										))
									) : (
										<EmptyState>No products sold in this period</EmptyState>
									)}
								</StatsList>
								{stats &&
									(stats.top_10_products_by_quantity?.length > 0 ||
										stats.top_10_products_by_revenue?.length > 0) && (
										<div className="mt-4 text-center">
											<button
												onClick={() => setShowAllProductsModal(true)}
												className="text-sm text-blue-600 hover:text-blue-800 font-medium"
											>
												Show More →
											</button>
										</div>
									)}
							</StatsSection>

							{/* Top Customers Section */}
							<StatsSection>
								<SectionHeader>
									<SectionTitle style={{ marginBottom: 0 }}>Top 10 Customers</SectionTitle>
									<FormControl size="small">
										<StyledSelect
											value={customerViewType}
											onChange={(e) =>
												setCustomerViewType(e.target.value as 'quantity' | 'revenue')
											}
										>
											<MenuItem value="quantity">By Quantity</MenuItem>
											<MenuItem value="revenue">By Amount</MenuItem>
										</StyledSelect>
									</FormControl>
								</SectionHeader>
								<StatsList>
									{customerViewType === 'quantity' ? (
										stats?.top_10_customers_by_quantity &&
										stats.top_10_customers_by_quantity.length > 0 ? (
											stats.top_10_customers_by_quantity.map((customer, index) => (
												<ListItem key={customer.customer_id}>
													<ListItemLabel>
														{index + 1}. {customer.customer_name}
													</ListItemLabel>
													<ListItemValue>
														{customer.total_quantity
															? customer.total_quantity.toLocaleString()
															: '0'}{' '}
														units
													</ListItemValue>
												</ListItem>
											))
										) : (
											<EmptyState>No customers in this period</EmptyState>
										)
									) : stats?.top_10_customers_by_revenue &&
									  stats.top_10_customers_by_revenue.length > 0 ? (
										stats.top_10_customers_by_revenue.map((customer, index) => (
											<ListItem key={customer.customer_id}>
												<ListItemLabel>
													{index + 1}. {customer.customer_name}
												</ListItemLabel>
												<ListItemValue>
													{currency}
													{customer.total_revenue
														? Number(customer.total_revenue).toFixed(2)
														: '0.00'}
												</ListItemValue>
											</ListItem>
										))
									) : (
										<EmptyState>No customers in this period</EmptyState>
									)}
								</StatsList>
							</StatsSection>
						</StatsGrid>
					</MobileOrderWrapper>

					{/* Pending Payment Orders Section - Order 4 on mobile */}
					<MobileOrderWrapper order={4}>
						<PendingOrdersSection>
							<SectionTitle style={{ margin: '40px 0px' }}>Pending Payment Orders</SectionTitle>
							{stats?.pending_payment_orders && stats.pending_payment_orders.length > 0 ? (
								<PendingOrdersTableWrapper>
									<PendingOrdersTable>
										<thead>
											<tr>
												<TableHeader>Date</TableHeader>
												<TableHeader>Customer</TableHeader>
												<TableHeader>Amount</TableHeader>
												<TableHeader>Action</TableHeader>
											</tr>
										</thead>
										<tbody>
											{stats.pending_payment_orders.map((order) => (
												<tr key={order.id}>
													<TableCell>{formatDate(order.order_date)}</TableCell>
													<TableCell>{order.customer_name}</TableCell>
													<TableCell>
														{currency}
														{order.order_amount
															? Number(order.order_amount).toFixed(2)
															: '0.00'}
													</TableCell>
													<TableCell>
														<ViewInvoiceButton
															onClick={() => handleViewInvoice(order.hash_id)}
														>
															View Invoice
														</ViewInvoiceButton>
													</TableCell>
												</tr>
											))}
										</tbody>
									</PendingOrdersTable>
								</PendingOrdersTableWrapper>
							) : (
								<EmptyState>No pending payment orders</EmptyState>
							)}
						</PendingOrdersSection>
					</MobileOrderWrapper>
				</AnalyticsFullGridWrapper>
			)}
			<AllProductsModal
				open={showAllProductsModal}
				onClose={() => setShowAllProductsModal(false)}
				fromDate={format(fromDate, 'yyyy-MM-dd')}
				toDate={format(toDate, 'yyyy-MM-dd')}
			/>
		</Wrapper>
	);
}
